import React, { useEffect, useState } from "react";
import { Typography, Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button } from "@mui/material";
import { TrendingUp, BarChart, MonetizationOn, CloudDownload } from "@mui/icons-material";
import { getPerformanceMetrics } from "../services/api";  // Placeholder for API function

const PerformanceMetrics = () => {
    const [metrics, setMetrics] = useState({
        totalCOAvoided: 1234567,
        clinkerReduction: 856432,
        carbonCreditsGenerated: 45678,
        waterSaved: { current: 234567, target: 250000 },
        energyEfficiency: { current: 89, target: 90 },
        wasteReduction: { current: 67, target: 70 }
    });

    useEffect(() => {
        fetchPerformanceMetrics();
    }, []);

    const fetchPerformanceMetrics = async () => {
        try {
            const data = await getPerformanceMetrics(); // Replace with real API call
            setMetrics(data);
        } catch (error) {
            console.error("Error fetching performance metrics", error);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, padding: "20px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    Performance Metrics
                </Typography>
                <Button variant="contained" startIcon={<CloudDownload />} sx={{ borderRadius: "8px" }}>
                    Export Report
                </Button>
            </Box>

            {/* KPI Stats */}
            <Grid container spacing={3}>
                {/* Total CO Avoided */}
                <Grid item xs={12} sm={4}>
                    <Card sx={{ boxShadow: 3, borderRadius: "12px", padding: "15px", border: "1px solid #E0E0E0" }}>
                        <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <TrendingUp sx={{ fontSize: 28, backgroundColor: "#DFF5E2", padding: "6px", borderRadius: "50%" }} />
                                <Typography variant="body2" sx={{ marginLeft: "8px", fontWeight: "bold" }}>Total CO Avoided</Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontWeight: "bold" }}>{metrics.totalCOAvoided.toLocaleString()} tons</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Clinker Reduction */}
                <Grid item xs={12} sm={4}>
                    <Card sx={{ boxShadow: 3, borderRadius: "12px", padding: "15px", border: "1px solid #E0E0E0" }}>
                        <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <BarChart sx={{ fontSize: 28, backgroundColor: "#D6E4FF", padding: "6px", borderRadius: "50%" }} />
                                <Typography variant="body2" sx={{ marginLeft: "8px", fontWeight: "bold" }}>Clinker Reduction</Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontWeight: "bold" }}>{metrics.clinkerReduction.toLocaleString()} tons</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Carbon Credits Generated */}
                <Grid item xs={12} sm={4}>
                    <Card sx={{ boxShadow: 3, borderRadius: "12px", padding: "15px", border: "1px solid #E0E0E0" }}>
                        <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <MonetizationOn sx={{ fontSize: 28, backgroundColor: "#F5D1FF", padding: "6px", borderRadius: "50%" }} />
                                <Typography variant="body2" sx={{ marginLeft: "8px", fontWeight: "bold" }}>Carbon Credits Generated</Typography>
                            </Box>
                            <Typography variant="h3" sx={{ fontWeight: "bold" }}>{metrics.carbonCreditsGenerated.toLocaleString()}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Regional CO Avoidance + Environmental Impact Metrics */}
            <Grid container spacing={3} sx={{ mt: 4 }}>
                {/* Regional CO Avoidance (Graph Placeholder) */}
                <Grid item xs={12} sm={6}>
                    <Card sx={{ boxShadow: 3, borderRadius: "12px", padding: "15px", border: "1px solid #E0E0E0", textAlign: "center" }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                                Regional CO Avoidance
                            </Typography>
                            <Box sx={{
                                width: "100%",
                                height: "200px",
                                backgroundColor: "#EAE6FF",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#6A5ACD"
                            }}>
                                <Typography variant="body2">Graph Placeholder</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Environmental Impact Metrics */}
                <Grid item xs={12} sm={6}>
                    <Card sx={{ boxShadow: 3, borderRadius: "12px", padding: "15px", border: "1px solid #E0E0E0" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>Environmental Impact Metrics</Typography>
                                <Typography variant="body2" sx={{ fontWeight: "bold", color: "green" }}>On Track</Typography>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: "bold" }}>Metric</TableCell>
                                            <TableCell sx={{ fontWeight: "bold" }}>Current</TableCell>
                                            <TableCell sx={{ fontWeight: "bold" }}>Target</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Water Saved</TableCell>
                                            <TableCell>{metrics.waterSaved.current.toLocaleString()} L</TableCell>
                                            <TableCell>{metrics.waterSaved.target.toLocaleString()} L</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Energy Efficiency</TableCell>
                                            <TableCell>{metrics.energyEfficiency.current}%</TableCell>
                                            <TableCell>{metrics.energyEfficiency.target}%</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Waste Reduction</TableCell>
                                            <TableCell>{metrics.wasteReduction.current}%</TableCell>
                                            <TableCell>{metrics.wasteReduction.target}%</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PerformanceMetrics;
